import { useState, useEffect, useRef, useCallback } from "react";
import confetti from "canvas-confetti";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Store, CheckCircle, ArrowRight, ArrowLeft, Loader2, 
  Key, Package, Code, Rocket, Copy, ExternalLink, Eye, EyeOff,
  AlertCircle, Users, Info, Building2, FileText, Shield, Link2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatINR, type Currency } from "@/lib/currency";
import sampleProductImage from "@/assets/sample-product.jpg";

interface MerchantInfo {
  id: string;
  store_name: string;
  shopify_store_domain: string;
  shopify_access_token: string | null;
  business_email: string | null;
  business_phone: string | null;
  business_address: string | null;
  gst_number: string | null;
  pan_number: string | null;
  default_deadline_hours: number | null;
  default_tax_rate: number | null;
  onboarding_completed_at: string | null;
}

interface Product {
  id: string;
  title: string;
  handle: string;
  status: string;
  image: string | null;
  price: string;
  shakatahEnabled: boolean;
}

interface BusinessDetails {
  business_email: string;
  business_phone: string;
  business_address: string;
  gst_number: string;
  pan_number: string;
  contact_person_name: string;
  contact_person_email: string;
  contact_person_phone: string;
  store_category: string;
  store_category_other: string;
  currency: Currency;
}

type OnboardingStep = "welcome" | "business" | "permissions" | "connect" | "products" | "widget" | "complete";

const STEPS: { key: OnboardingStep; title: string; icon: React.ReactNode }[] = [
  { key: "welcome", title: "Welcome", icon: <Store className="h-4 w-4" /> },
  { key: "business", title: "Business Info", icon: <Building2 className="h-4 w-4" /> },
  { key: "permissions", title: "Permissions", icon: <Shield className="h-4 w-4" /> },
  { key: "connect", title: "Connect Store", icon: <Link2 className="h-4 w-4" /> },
  { key: "products", title: "Products", icon: <Package className="h-4 w-4" /> },
  { key: "widget", title: "Widget", icon: <Code className="h-4 w-4" /> },
  { key: "complete", title: "Complete", icon: <Rocket className="h-4 w-4" /> },
];

const MerchantOnboarding = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  
  // Initialize step from URL param or default to welcome
  const initialStep = (searchParams.get("step") as OnboardingStep) || "welcome";
  const [step, setStep] = useState<OnboardingStep>(initialStep);
  const [merchant, setMerchant] = useState<MerchantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [noMerchantRecord, setNoMerchantRecord] = useState(false);
  const [connectingShopify, setConnectingShopify] = useState(false);
  
  // Business details state
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails>({
    business_email: "",
    business_phone: "",
    business_address: "",
    gst_number: "",
    pan_number: "",
    contact_person_name: "",
    contact_person_email: "",
    contact_person_phone: "",
    store_category: "",
    store_category_other: "",
    currency: "USD",
  });
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [permissionsAccepted, setPermissionsAccepted] = useState(false);
  
  // Connect step state
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionSuccess, setConnectionSuccess] = useState(false);
  const [connectionVerified, setConnectionVerified] = useState(false);
  
  // Products step state
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [togglingProduct, setTogglingProduct] = useState<string | null>(null);
  
  // Widget step state
  const [copied, setCopied] = useState(false);
  const [widgetPosition, setWidgetPosition] = useState<"image-top-left" | "next-to-title">("image-top-left");
  const [installingWidget, setInstallingWidget] = useState(false);
  const [widgetInstalled, setWidgetInstalled] = useState(false);
  
  // Manual token entry state
  const [manualToken, setManualToken] = useState("");
  const [showManualToken, setShowManualToken] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  
  // Ref to prevent multiple OAuth initiations
  const oauthInitiatedRef = useRef(false);

  // Handle Shopify app install redirect - auto-initiate OAuth
  useEffect(() => {
    // Prevent multiple executions
    if (oauthInitiatedRef.current) return;
    
    const shop = searchParams.get("shop");
    const hmac = searchParams.get("hmac");
    const oauthSuccess = searchParams.get("oauth_success");
    const oauthError = searchParams.get("oauth_error");

    // If coming from Shopify app install (has shop + hmac but not oauth callback)
    if (shop && hmac && !oauthSuccess && !oauthError) {
      console.log("Detected Shopify app install for:", shop);
      
      // Store shop domain for after login
      sessionStorage.setItem("pending_shopify_shop", shop);
      
      // If user is logged in and has merchant, check if already onboarded
      if (user && merchant) {
        // If merchant is already onboarded, redirect to dashboard
        if (merchant.onboarding_completed_at) {
          console.log("Merchant already onboarded, redirecting to dashboard");
          // Break out of iframe if needed and redirect to dashboard
          const dashboardUrl = `${window.location.origin}/merchant-dashboard`;
          if (window.top !== window.self) {
            window.top!.location.href = dashboardUrl;
          } else {
            navigate("/merchant-dashboard", { replace: true });
          }
          return;
        }
        
        // Mark as initiated to prevent duplicate opens
        oauthInitiatedRef.current = true;
        
        // Build OAuth URL directly to Shopify (bypasses edge function to avoid iframe issues)
        const shopDomain = shop.replace('.myshopify.com', '').replace(/^https?:\/\//, '');
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const callbackUrl = `${supabaseUrl}/functions/v1/shopify-oauth-callback`;
        
        const sessionId = crypto.randomUUID();
        const state = `${sessionId}:${merchant.id}`;
        const scopes = 'read_products,write_products,read_inventory,write_inventory,write_draft_orders,read_orders,read_script_tags,write_script_tags';
        const clientId = '0d13b47541e472e1801440ed59c58fbb';
        
        const oauthUrl = new URL(`https://${shopDomain}.myshopify.com/admin/oauth/authorize`);
        oauthUrl.searchParams.set('client_id', clientId);
        oauthUrl.searchParams.set('scope', scopes);
        oauthUrl.searchParams.set('redirect_uri', callbackUrl);
        oauthUrl.searchParams.set('state', state);
        
        // CRITICAL: Break out of Shopify's iframe completely to avoid cookie issues
        if (window.top !== window.self) {
          // We're inside Shopify iframe - redirect the top window to break out
          window.top!.location.href = oauthUrl.toString();
        } else {
          window.location.href = oauthUrl.toString();
        }
        return;
      }
    }
  }, [searchParams, user, merchant, navigate]);

  // Handle OAuth callback result
  useEffect(() => {
    const oauthSuccess = searchParams.get("oauth_success");
    const oauthError = searchParams.get("oauth_error");
    const shopName = searchParams.get("shop");
    const currentStep = searchParams.get("step");

    if (oauthSuccess === "true") {
      toast.success(`Connected to "${shopName || "your store"}"!`);
      setConnectionSuccess(true);
      // Clear OAuth params but preserve step, then move to products
      setSearchParams({ step: "products" });
      setStep("products");
      // Fetch products
      if (products.length === 0) {
        fetchProducts();
      }
    } else if (oauthError) {
      toast.error(`Connection failed: ${oauthError}`);
      // Preserve step when clearing error params
      setSearchParams(currentStep ? { step: currentStep } : {});
    }
  }, [searchParams]);

  useEffect(() => {
    if (!authLoading && !user) {
      // Preserve current URL params when redirecting to login
      const currentUrl = window.location.pathname + window.location.search;
      navigate(`/merchant-login?redirect=${encodeURIComponent(currentUrl)}`);
    } else if (user) {
      fetchMerchantInfo();
    }
  }, [user, authLoading, navigate]);

  const fetchMerchantInfo = async () => {
    try {
      const { data: merchantId } = await supabase.rpc('get_user_merchant_id', { _user_id: user!.id });
      
      if (!merchantId) {
        // No merchant record - check if we have pending Shopify data to create one
        const pendingShop = sessionStorage.getItem("pending_shopify_shop");
        const pendingToken = sessionStorage.getItem("pending_shopify_token");
        const pendingShopName = sessionStorage.getItem("pending_shopify_name");
        
        if (pendingShop && pendingToken) {
          console.log("Creating new Shopify merchant for user with pending data:", pendingShop);
          
          // Create merchant record with Shopify data
          const { data: newMerchant, error: createError } = await supabase
            .from("merchants")
            .insert({
              shopify_store_domain: pendingShop,
              shopify_access_token: pendingToken,
              store_name: pendingShopName || pendingShop.replace(".myshopify.com", ""),
              platform_type: "shopify",
              plan: "trial",
              subscription_status: "trialing",
            })
            .select()
            .single();
          
          if (createError || !newMerchant) {
            console.error("Failed to create Shopify merchant:", createError);
            toast.error("Failed to set up your store. Please try again.");
            setNoMerchantRecord(true);
            setLoading(false);
            return;
          }
          
          // Link user to merchant
          const { error: linkError } = await supabase
            .from("merchant_users")
            .insert({
              user_id: user!.id,
              merchant_id: newMerchant.id,
              role: "super_admin",
            });
          
          if (linkError) {
            console.error("Failed to link user to merchant:", linkError);
          }
          
          // Clear pending data
          sessionStorage.removeItem("pending_shopify_shop");
          sessionStorage.removeItem("pending_shopify_token");
          sessionStorage.removeItem("pending_shopify_name");
          
          toast.success("Shopify store connected! Let's complete your setup.");
          
          // Set the new merchant and continue
          setMerchant({
            id: newMerchant.id,
            store_name: newMerchant.store_name,
            shopify_store_domain: newMerchant.shopify_store_domain,
            shopify_access_token: newMerchant.shopify_access_token,
            business_email: null,
            business_phone: null,
            business_address: null,
            gst_number: null,
            pan_number: null,
            default_deadline_hours: null,
            default_tax_rate: null,
            onboarding_completed_at: null,
          });
          setConnectionSuccess(true); // Already connected via OAuth
          setLoading(false);
          return;
        }
        
        // No pending data - show error state
        console.log("No merchant found for user and no pending Shopify data");
        setNoMerchantRecord(true);
        setLoading(false);
        return;
      }

      const { data: merchantData, error } = await supabase
        .from("merchants")
        .select("id, store_name, shopify_store_domain, shopify_access_token, business_email, business_phone, business_address, gst_number, pan_number, default_deadline_hours, default_tax_rate, onboarding_completed_at")
        .eq("id", merchantId)
        .single();

      if (error || !merchantData) {
        console.error("Failed to load merchant:", error);
        toast.error("Failed to load merchant information");
        setNoMerchantRecord(true);
        setLoading(false);
        return;
      }

      // If merchant is already onboarded, redirect to dashboard immediately
      if (merchantData.onboarding_completed_at) {
        console.log("Merchant already onboarded, redirecting to dashboard");
        navigate("/merchant-dashboard", { replace: true });
        return;
      }

      setMerchant(merchantData);
      
      // Pre-fill business details if already saved
      setBusinessDetails({
        business_email: merchantData.business_email || user?.email || "",
        business_phone: merchantData.business_phone || "",
        business_address: merchantData.business_address || "",
        gst_number: merchantData.gst_number || "",
        pan_number: merchantData.pan_number || "",
        contact_person_name: (merchantData as any).contact_person_name || "",
        contact_person_email: (merchantData as any).contact_person_email || "",
        contact_person_phone: (merchantData as any).contact_person_phone || "",
        store_category: (merchantData as any).store_category || "",
        store_category_other: "",
        currency: ((merchantData as any).currency as Currency) || "USD",
      });
      
      // If already has token, check if we should skip to products
      if (merchantData.shopify_access_token) {
        setConnectionSuccess(true);
      }
    } catch (error) {
      console.error("Error fetching merchant:", error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBusinessDetails = async () => {
    if (!merchant) return;
    if (!termsAccepted) {
      toast.error("Please accept the terms and conditions");
      return;
    }
    if (!businessDetails.business_email || !businessDetails.business_phone || !businessDetails.contact_person_name || !businessDetails.contact_person_email || !businessDetails.store_category) {
      toast.error("Please fill in required fields");
      return;
    }
    if (businessDetails.store_category === "other" && !businessDetails.store_category_other) {
      toast.error("Please specify your store category");
      return;
    }

    setSavingBusiness(true);
    try {
      const finalCategory = businessDetails.store_category === "other" 
        ? businessDetails.store_category_other 
        : businessDetails.store_category;

      const { error } = await supabase
        .from("merchants")
        .update({
          business_email: businessDetails.business_email,
          business_phone: businessDetails.business_phone,
          business_address: businessDetails.business_address,
          gst_number: businessDetails.gst_number || null,
          pan_number: businessDetails.pan_number || null,
          contact_person_name: businessDetails.contact_person_name,
          contact_person_email: businessDetails.contact_person_email,
          contact_person_phone: businessDetails.contact_person_phone || null,
          store_category: finalCategory,
          currency: businessDetails.currency,
          terms_accepted_at: new Date().toISOString(),
        } as any)
        .eq("id", merchant.id);

      if (error) throw error;

      toast.success("Business details saved!");
      handleStepChange("permissions");
    } catch (error) {
      console.error("Error saving business details:", error);
      toast.error("Failed to save details");
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleConnectShopify = () => {
    if (!merchant) return;
    
    setConnectingShopify(true);
    
    // Build OAuth URL directly to Shopify (bypasses edge function to avoid iframe issues)
    const shopDomain = merchant.shopify_store_domain.replace('.myshopify.com', '').replace(/^https?:\/\//, '');
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const callbackUrl = `${supabaseUrl}/functions/v1/shopify-oauth-callback`;
    
    // Generate a unique session ID for state parameter
    const sessionId = crypto.randomUUID();
    const state = `${sessionId}:${merchant.id}`;
    
    // Shopify OAuth scopes
    const scopes = 'read_products,write_products,read_inventory,write_inventory,write_draft_orders,read_orders,read_script_tags,write_script_tags';
    
    // Shopify API Key (Client ID) - this is public and safe to expose
    const clientId = '0d13b47541e472e1801440ed59c58fbb';
    
    const oauthUrl = new URL(`https://${shopDomain}.myshopify.com/admin/oauth/authorize`);
    oauthUrl.searchParams.set('client_id', clientId);
    oauthUrl.searchParams.set('scope', scopes);
    oauthUrl.searchParams.set('redirect_uri', callbackUrl);
    oauthUrl.searchParams.set('state', state);
    
    // CRITICAL: Break out of Shopify's iframe if present
    if (window.top !== window.self) {
      window.top!.location.href = oauthUrl.toString();
    } else {
      window.location.href = oauthUrl.toString();
    }
  };

  const testConnection = async () => {
    if (!merchant) return;
    
    setTestingConnection(true);
    try {
      // Use edge function to test connection (avoids CORS issues)
      const { data, error } = await supabase.functions.invoke("manage-product-metafield", {
        body: { 
          merchantId: merchant.id, 
          action: "test-connection" 
        },
      });

      if (error) throw error;
      
      if (data?.success) {
        setConnectionSuccess(true);
        setConnectionVerified(true);
        // Update local merchant state with the store name from Shopify
        if (data.shopName) {
          setMerchant(prev => prev ? { ...prev, store_name: data.shopName } : null);
        }
        toast.success("Connection verified! 🎉");
        
        // Fire confetti celebration!
        const duration = 3000;
        const end = Date.now() + duration;
        const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];
        
        const frame = () => {
          confetti({
            particleCount: 3,
            angle: 60,
            spread: 55,
            origin: { x: 0 },
            colors: colors
          });
          confetti({
            particleCount: 3,
            angle: 120,
            spread: 55,
            origin: { x: 1 },
            colors: colors
          });
          if (Date.now() < end) {
            requestAnimationFrame(frame);
          }
        };
        frame();
        
        // Auto-navigate to products after showing success message (longer delay for celebration)
        setTimeout(() => {
          handleStepChange("products");
        }, 8000);
      } else if (data?.error) {
        toast.error(data.error);
        setConnectionSuccess(false);
      } else {
        toast.error("Connection test failed");
        setConnectionSuccess(false);
      }
    } catch (error: any) {
      console.error("Connection test error:", error);
      toast.error(error.message || "Failed to test connection");
      setConnectionSuccess(false);
    } finally {
      setTestingConnection(false);
    }
  };

  const fetchProducts = async () => {
    if (!merchant) return;
    
    setLoadingProducts(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-product-metafield", {
        body: { merchantId: merchant.id, action: "list" },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setProducts(data.products || []);
    } catch (error: any) {
      console.error("Error fetching products:", error);
      toast.error(error.message || "Failed to fetch products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleToggleProduct = async (productId: string, currentEnabled: boolean) => {
    if (!merchant) {
      toast.error("Merchant not loaded");
      return;
    }
    
    const newEnabled = !currentEnabled;
    
    // Optimistic update - instant UI response
    setProducts(prev => 
      prev.map(p => p.id === productId ? { ...p, shakatahEnabled: newEnabled } : p)
    );
    
    try {
      const { data, error } = await supabase.functions.invoke("manage-product-metafield", {
        body: { 
          merchantId: merchant.id, 
          action: "toggle",
          productId,
          enabled: newEnabled,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      // Success toast only - UI already updated
      toast.success(newEnabled ? "Split Payment enabled!" : "Split Payment disabled");
    } catch (error: any) {
      // Revert on failure
      setProducts(prev => 
        prev.map(p => p.id === productId ? { ...p, shakatahEnabled: currentEnabled } : p)
      );
      toast.error(error.message || "Failed to update product");
    }
  };

  const handleBulkToggle = async (enableAll: boolean) => {
    if (!merchant || products.length === 0) return;
    
    const productsToUpdate = products.filter(p => p.shakatahEnabled !== enableAll);
    if (productsToUpdate.length === 0) {
      toast.info(enableAll ? "All products already enabled" : "All products already disabled");
      return;
    }

    // Optimistic update
    setProducts(prev => prev.map(p => ({ ...p, shakatahEnabled: enableAll })));
    
    try {
      const { data, error } = await supabase.functions.invoke("manage-product-metafield", {
        body: { 
          merchantId: merchant.id, 
          action: "bulk-toggle",
          productIds: productsToUpdate.map(p => p.id),
          enabled: enableAll,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      toast.success(enableAll ? "All products enabled!" : "All products disabled");
    } catch (error: any) {
      // Revert on failure
      setProducts(prev => prev.map(p => {
        const original = productsToUpdate.find(pt => pt.id === p.id);
        if (original) {
          return { ...p, shakatahEnabled: !enableAll };
        }
        return p;
      }));
      toast.error(error.message || "Failed to update products");
    }
  };

  const getWidgetCode = () => {
    const baseUrl = window.location.origin;
    return `<!-- Shakatah Split Payment Widget -->
<script>
  window.ShakatahConfig = {
    merchantId: "${merchant?.id}",
    buttonText: "Split Payment with Friends",
    buttonColor: "#8B5CF6"
  };
</script>
<script src="${baseUrl}/shakatah-widget.js" async></script>`;
  };

  const copyWidgetCode = () => {
    navigator.clipboard.writeText(getWidgetCode());
    setCopied(true);
    toast.success("Widget code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleInstallWidget = async () => {
    if (!merchant) return;
    
    setInstallingWidget(true);
    try {
      const { data, error } = await supabase.functions.invoke("install-widget", {
        body: { 
          merchantId: merchant.id, 
          action: "install",
          position: widgetPosition,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setWidgetInstalled(true);
      toast.success("Widget installed successfully!");
      
      // Move to complete step after successful installation
      handleStepChange("complete");
    } catch (error: any) {
      console.error("Error installing widget:", error);
      // Check if it's a scope/permission error
      const errorMsg = error.message || "Failed to install widget";
      if (errorMsg.includes("scope") || errorMsg.includes("approval") || errorMsg.includes("access denied")) {
        toast.error("Widget installation failed - you may need to reconnect with updated permissions. Proceeding to complete setup.", {
          duration: 5000,
        });
      } else {
        toast.error(`${errorMsg}. You can install manually from your dashboard.`, {
          duration: 5000,
        });
      }
      // Navigate to complete step even on error so user can proceed
      handleStepChange("complete");
    } finally {
      setInstallingWidget(false);
    }
  };

  const handleStepChange = (newStep: OnboardingStep) => {
    // Fetch products when entering products step
    if (newStep === "products" && products.length === 0) {
      fetchProducts();
    }
    setStep(newStep);
    // Persist step to URL for OAuth redirect preservation
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("step", newStep);
      return newParams;
    });
  };

  const handleComplete = async () => {
    if (!merchant) return;

    // Start trial and mark onboarding complete (30 days trial period)
    const trialStart = new Date();
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + 30);

    await supabase
      .from("merchants")
      .update({
        plan: "trial",
        subscription_status: "trialing",
        trial_started_at: trialStart.toISOString(),
        trial_ends_at: trialEnd.toISOString(),
        splits_this_month: 0,
        onboarding_completed_at: new Date().toISOString(),
      })
      .eq("id", merchant.id);

    navigate("/merchant-dashboard");
  };

  const currentStepIndex = STEPS.findIndex(s => s.key === step);
  const progressPercent = ((currentStepIndex + 1) / STEPS.length) * 100;
  const enabledProductsCount = products.filter(p => p.shakatahEnabled).length;

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show error UI if no merchant record found (prevents infinite loop)
  if (noMerchantRecord || !merchant) {
    const handleSignOut = async () => {
      await supabase.auth.signOut();
      navigate("/merchant-login?tab=signup");
    };

    const handleRetry = () => {
      setNoMerchantRecord(false);
      setLoading(true);
      window.location.reload();
    };

    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Account Setup Issue</CardTitle>
            <CardDescription>
              Your account wasn't set up correctly. Please sign out and create a new account, or contact support.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={handleRetry} className="w-full" variant="default">
              Try Again
            </Button>
            <Button onClick={handleSignOut} className="w-full" variant="outline">
              Sign Out & Start Fresh
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">Setup Your Store</h1>
            <span className="text-sm text-muted-foreground">
              Step {currentStepIndex + 1} of {STEPS.length}
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
          <div className="flex justify-between mt-2">
            {STEPS.map((s, index) => (
              <button
                key={s.key}
                onClick={() => {
                  if (index <= currentStepIndex) {
                    handleStepChange(s.key);
                  }
                }}
                className={`flex flex-col items-center gap-1 text-xs transition-colors ${
                  index <= currentStepIndex
                    ? "text-primary cursor-pointer"
                    : "text-muted-foreground cursor-not-allowed"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  index < currentStepIndex
                    ? "bg-primary text-primary-foreground"
                    : index === currentStepIndex
                    ? "bg-primary/20 text-primary border-2 border-primary"
                    : "bg-muted"
                }`}>
                  {index < currentStepIndex ? <CheckCircle className="h-4 w-4" /> : s.icon}
                </div>
                <span className="hidden sm:block">{s.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Step Content */}
        {step === "welcome" && (
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Store className="h-8 w-8 text-primary" />
              </div>
              {/* TODO: Enable returning merchant detection after finalizing onboarding flow
              {(merchant.shopify_access_token || merchant.business_email) ? (
                <>
                  <CardTitle className="text-2xl">Welcome Back! 👋</CardTitle>
                  <CardDescription className="text-base">
                    Happy to see you again! Pick up where you left off and let's make group gifting simple for <strong>{merchant.store_name}</strong>
                  </CardDescription>
                </>
              ) : ( ... )}
              */}
              <CardTitle className="text-2xl">Welcome to Shakatah!</CardTitle>
              <CardDescription className="text-base">
                Let's set up split payments for <strong>{merchant.store_name}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4">
                <div className="flex items-start gap-4 p-4 rounded-lg border bg-card">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Business Details</h3>
                    <p className="text-sm text-muted-foreground">
                      Tell us about your business for invoicing and compliance
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg border bg-card">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Key className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Connect Shopify Store</h3>
                    <p className="text-sm text-muted-foreground">
                      Securely connect your store to enable split payments
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg border bg-card">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Enable Products</h3>
                    <p className="text-sm text-muted-foreground">
                      Choose which products support split payments
                    </p>
                  </div>
                </div>
              </div>
              <Button 
                onClick={() => handleStepChange("business")} 
                className="w-full"
                size="lg"
              >
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "business" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Business Information
              </CardTitle>
              <CardDescription>
                This information is used for invoicing, compliance, and notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Contact Details
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="business_email">Business Email *</Label>
                    <Input
                      id="business_email"
                      type="email"
                      value={businessDetails.business_email}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, business_email: e.target.value }))}
                      placeholder="billing@yourstore.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_phone">Business Phone *</Label>
                    <Input
                      id="business_phone"
                      type="tel"
                      value={businessDetails.business_phone}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, business_phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_address">Business Address</Label>
                  <Textarea
                    id="business_address"
                    value={businessDetails.business_address}
                    onChange={(e) => setBusinessDetails(prev => ({ ...prev, business_address: e.target.value }))}
                    placeholder="Full business address for invoicing"
                    rows={2}
                  />
                </div>
              </div>

              {/* Tax Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Tax Information (Optional)
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="gst_number">GST Number</Label>
                    <Input
                      id="gst_number"
                      value={businessDetails.gst_number}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }))}
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pan_number">PAN Number</Label>
                    <Input
                      id="pan_number"
                      value={businessDetails.pan_number}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))}
                      placeholder="ABCDE1234F"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Person */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Contact Person
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="contact_person_name">Contact Name *</Label>
                    <Input
                      id="contact_person_name"
                      value={businessDetails.contact_person_name}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, contact_person_name: e.target.value }))}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_person_email">Contact Email *</Label>
                    <Input
                      id="contact_person_email"
                      type="email"
                      value={businessDetails.contact_person_email}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, contact_person_email: e.target.value }))}
                      placeholder="john@yourstore.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_person_phone">Contact Phone (Optional)</Label>
                  <Input
                    id="contact_person_phone"
                    type="tel"
                    value={businessDetails.contact_person_phone}
                    onChange={(e) => setBusinessDetails(prev => ({ ...prev, contact_person_phone: e.target.value }))}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>

              {/* Store Category */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  What Are You Selling?
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="store_category">Store Category *</Label>
                  <Select
                    value={businessDetails.store_category}
                    onValueChange={(v) => setBusinessDetails(prev => ({ ...prev, store_category: v, store_category_other: v !== "other" ? "" : prev.store_category_other }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your store category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fashion_apparel">Fashion & Apparel</SelectItem>
                      <SelectItem value="health_wellness">Health & Wellness</SelectItem>
                      <SelectItem value="beauty_cosmetics">Beauty & Cosmetics</SelectItem>
                      <SelectItem value="electronics">Electronics</SelectItem>
                      <SelectItem value="food_beverages">Food & Beverages</SelectItem>
                      <SelectItem value="home_decor">Home Decor</SelectItem>
                      <SelectItem value="pet_supplies">Pet Supplies</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {businessDetails.store_category === "other" && (
                  <div className="space-y-2">
                    <Label htmlFor="store_category_other">Please specify *</Label>
                    <Input
                      id="store_category_other"
                      value={businessDetails.store_category_other}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, store_category_other: e.target.value }))}
                      placeholder="e.g., Sports Equipment, Jewelry, etc."
                    />
                  </div>
                )}
              </div>

              {/* Currency Selection */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Currency Settings
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="currency">Store Currency *</Label>
                  <Select
                    value={businessDetails.currency}
                    onValueChange={(v) => setBusinessDetails(prev => ({ ...prev, currency: v as Currency }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">🇺🇸 USD - US Dollar ($)</SelectItem>
                      <SelectItem value="INR">🇮🇳 INR - Indian Rupee (₹)</SelectItem>
                      <SelectItem value="EUR">🇪🇺 EUR - Euro (€)</SelectItem>
                      <SelectItem value="GBP">🇬🇧 GBP - British Pound (£)</SelectItem>
                      <SelectItem value="AUD">🇦🇺 AUD - Australian Dollar (A$)</SelectItem>
                      <SelectItem value="CAD">🇨🇦 CAD - Canadian Dollar (C$)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    This currency will be used for all financial displays in your dashboard.
                  </p>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="p-4 rounded-lg border bg-muted/50 space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                  />
                  <Label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                    I agree to Shakatah's{" "}
                    <a href="/terms" target="_blank" className="text-primary hover:underline">
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a href="/privacy" target="_blank" className="text-primary hover:underline">
                      Privacy Policy
                    </a>
                    . I understand that Shakatah will process payments on behalf of my customers and that I am responsible for product fulfillment.
                  </Label>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => handleStepChange("welcome")}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={handleSaveBusinessDetails}
                  disabled={savingBusiness || !termsAccepted || !businessDetails.business_email || !businessDetails.business_phone || !businessDetails.contact_person_name || !businessDetails.contact_person_email || !businessDetails.store_category || (businessDetails.store_category === "other" && !businessDetails.store_category_other)}
                  className="flex-1"
                >
                  {savingBusiness ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "permissions" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                How This App Connects to Your Store
              </CardTitle>
              <CardDescription>
                Understanding what access you're granting
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
                <p>
                  When you install this app, Shopify will ask you to approve access to certain parts of your store.
                  This secure connection lets the app work properly and deliver the features you expect.
                </p>
                
                <div className="space-y-3">
                  <h3 className="font-medium text-foreground">What this means for you:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>You stay fully in control — access is only granted after your approval</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>The app can only access what it needs to function</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>We cannot see your Shopify login details or password</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>You can remove the app (and its access) at any time from your Shopify admin</span>
                    </li>
                  </ul>
                </div>

                <div className="p-4 rounded-lg border bg-muted/50 space-y-3">
                  <h3 className="font-medium text-foreground flex items-center gap-2">
                    <Key className="h-4 w-4 text-primary" />
                    What access you're granting:
                  </h3>
                  <p>
                    The app may read or update specific store data such as products, orders, or settings — only as required for the app's features.
                    We never access anything beyond the permissions you approve.
                  </p>
                  <p className="text-xs">
                    Shopify handles this connection securely using its built-in authorization system.
                  </p>
                </div>
              </div>

              {/* Terms Agreement */}
              <div className="p-4 rounded-lg border bg-card space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="permissions-agreement"
                    checked={permissionsAccepted}
                    onCheckedChange={(checked) => setPermissionsAccepted(checked === true)}
                  />
                  <Label htmlFor="permissions-agreement" className="text-sm leading-relaxed cursor-pointer">
                    I understand and agree to the access permissions described above
                  </Label>
                </div>
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <FileText className="h-4 w-4" />
                  View complete Terms and Conditions
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => handleStepChange("business")}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={() => handleStepChange("connect")}
                  disabled={!permissionsAccepted}
                  className="flex-1"
                >
                  Continue to Connect Store
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "connect" && (
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                {(connectionSuccess || merchant.shopify_access_token) ? (
                  <CheckCircle className="h-8 w-8 text-primary" />
                ) : (
                  <Store className="h-8 w-8 text-primary" />
                )}
              </div>
              <CardTitle className="text-2xl">
                {(connectionSuccess || merchant.shopify_access_token) 
                  ? "Almost There!" 
                  : "One Step Away! 🎁"
                }
              </CardTitle>
              <CardDescription className="text-base">
                {(connectionSuccess || merchant.shopify_access_token)
                  ? "Just verify your connection and you're ready to go!"
                  : "You're about to make group gifting simple for your customers"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!connectionSuccess && !merchant.shopify_access_token ? (
                <Tabs defaultValue="quick" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="quick">Quick Connect</TabsTrigger>
                    <TabsTrigger value="manual">Advanced Setup</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="quick" className="space-y-6 pt-4">
                    <div className="text-center py-6 space-y-6">
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold">Connect {merchant.shopify_store_domain.replace('.myshopify.com', '')}</h3>
                        <p className="text-muted-foreground max-w-md mx-auto">
                          Click the button below and Shopify will guide you through securely connecting your store. It only takes a few seconds!
                        </p>
                      </div>
                      <Button 
                        onClick={handleConnectShopify}
                        disabled={connectingShopify}
                        size="lg"
                        className="gap-2"
                      >
                        {connectingShopify ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Link2 className="h-4 w-4" />
                        )}
                        Connect My Store
                      </Button>
                    </div>

                    <Alert className="border-primary/30 bg-primary/5">
                      <Info className="h-4 w-4 text-primary" />
                      <AlertDescription className="text-muted-foreground">
                        <strong className="text-foreground">Note:</strong> Your store needs to be pre-approved by Shakatah. If you haven't been approved yet, please contact us first.
                      </AlertDescription>
                    </Alert>
                  </TabsContent>

                  <TabsContent value="manual" className="space-y-6 pt-4">
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        Prefer to manage your own credentials? Use this method to connect with your Shopify Admin API token.
                      </AlertDescription>
                    </Alert>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="access_token">Shopify Admin API Access Token *</Label>
                        <div className="relative">
                          <Input
                            id="access_token"
                            type={showManualToken ? "text" : "password"}
                            value={manualToken}
                            onChange={(e) => setManualToken(e.target.value)}
                            placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                            className="pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                            onClick={() => setShowManualToken(!showManualToken)}
                          >
                            {showManualToken ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Your token is encrypted and stored securely
                        </p>
                      </div>

                      <Button 
                        onClick={async () => {
                          if (!manualToken.trim()) {
                            toast.error("Please enter an access token");
                            return;
                          }
                          setSavingToken(true);
                          try {
                            const testResponse = await fetch(
                              `https://${merchant.shopify_store_domain}/admin/api/2024-01/shop.json`,
                              {
                                headers: {
                                  "X-Shopify-Access-Token": manualToken.trim(),
                                  "Content-Type": "application/json",
                                },
                              }
                            );
                            
                            if (!testResponse.ok) {
                              if (testResponse.status === 401) {
                                toast.error("Invalid token - please check and try again");
                              } else {
                                toast.error(`Connection failed (${testResponse.status})`);
                              }
                              return;
                            }

                            const shopData = await testResponse.json();
                            const shopName = shopData.shop?.name || merchant.store_name;
                            
                            // Update token AND store name from Shopify
                            const { error } = await supabase
                              .from("merchants")
                              .update({ 
                                shopify_access_token: manualToken.trim(),
                                store_name: shopName,
                              })
                              .eq("id", merchant.id);

                            if (error) throw error;

                            // Update local state with new store name
                            setMerchant(prev => prev ? { ...prev, shopify_access_token: manualToken.trim(), store_name: shopName } : null);
                            setConnectionSuccess(true);
                            toast.success(`Connected to "${shopName}"!`);
                          } catch (error) {
                            console.error("Error saving token:", error);
                            toast.error("Failed to save token");
                          } finally {
                            setSavingToken(false);
                          }
                        }}
                        disabled={savingToken || !manualToken.trim()}
                        className="w-full"
                        size="lg"
                      >
                        {savingToken ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Key className="h-4 w-4 mr-2" />
                        )}
                        Connect Store
                      </Button>
                    </div>

                    <div className="p-4 rounded-lg border bg-muted/50 space-y-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <Info className="h-4 w-4 text-primary" />
                        How to get your Access Token
                      </h4>
                      <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                        <li>Go to your <a href={`https://${merchant.shopify_store_domain}/admin/settings/apps/development`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Shopify Admin → Settings → Apps → Develop apps</a></li>
                        <li>Create a new app or select an existing one</li>
                        <li>Under "API credentials", click "Configure Admin API scopes"</li>
                        <li>Enable: <code className="bg-muted px-1 rounded">read_products</code>, <code className="bg-muted px-1 rounded">write_products</code>, <code className="bg-muted px-1 rounded">read_inventory</code>, <code className="bg-muted px-1 rounded">write_inventory</code>, <code className="bg-muted px-1 rounded">write_draft_orders</code>, <code className="bg-muted px-1 rounded">read_orders</code></li>
                        <li>Install the app and copy the Admin API access token</li>
                      </ol>
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (connectionSuccess || merchant.shopify_access_token) ? (
                <>
                  <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
                        <Store className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">{merchant.store_name || merchant.shopify_store_domain.replace('.myshopify.com', '')}</p>
                        <p className="text-sm text-muted-foreground">Store connected successfully</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-green-600 border-green-500/50">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Ready
                    </Badge>
                  </div>

                  {!connectionVerified ? (
                    <div className="space-y-4">
                      <p className="text-center text-muted-foreground">
                        Click verify to confirm everything is working correctly
                      </p>
                      <Button
                        onClick={testConnection}
                        disabled={testingConnection}
                        className="w-full"
                        size="lg"
                      >
                        {testingConnection ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <CheckCircle className="h-4 w-4 mr-2" />
                        )}
                        {testingConnection ? "Checking connection..." : "Verify Connection"}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-6 py-4">
                      {/* Big Celebration Message */}
                      <div className="text-center space-y-4">
                        <div className="mx-auto w-24 h-24 bg-gradient-to-br from-green-500/20 to-primary/20 rounded-full flex items-center justify-center animate-pulse">
                          <span className="text-5xl">🎉</span>
                        </div>
                        <h2 className="text-2xl font-bold text-foreground">
                          You're All Set!
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-sm mx-auto">
                          Your store is now connected and ready for group gifting magic!
                        </p>
                      </div>
                      
                      <div className="p-6 rounded-xl bg-gradient-to-r from-primary/10 via-purple-500/10 to-pink-500/10 border border-primary/20 text-center space-y-3">
                        <p className="font-medium text-foreground">
                          🛍️ Customers can now split purchases with friends & family
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Birthday gifts, group buys, special occasions — you've just opened up a whole new way for people to shop together!
                        </p>
                      </div>

                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        <span>Taking you to choose your products...</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Something went wrong. Please try connecting again using one of the options above.
                  </AlertDescription>
                </Alert>
              )}

              {/* Only show navigation buttons when not connected OR when in not-connected state */}
              {!(connectionSuccess || merchant.shopify_access_token) && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => handleStepChange("permissions")}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                </div>
              )}
              
              {/* Show just Back button when connected (Verify is the main action) */}
              {(connectionSuccess || merchant.shopify_access_token) && !connectionVerified && (
                <Button 
                  variant="ghost" 
                  onClick={() => handleStepChange("permissions")}
                  className="w-full"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Go Back to Permissions
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {step === "products" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Enable Products for Split Payments
              </CardTitle>
              <CardDescription>
                Choose which products can be purchased using split payments
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingProducts ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : products.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No products found in your store. Add products in Shopify first, then return here.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg bg-muted">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {enabledProductsCount} of {products.length} products enabled
                      </span>
                      <Badge variant={enabledProductsCount > 0 ? "default" : "secondary"}>
                        {enabledProductsCount > 0 ? "Active" : "None enabled"}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkToggle(true)}
                        disabled={enabledProductsCount === products.length}
                      >
                        Enable All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkToggle(false)}
                        disabled={enabledProductsCount === 0}
                      >
                        Disable All
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {products.map((product) => (
                      <div
                        key={product.id}
                        className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
                          product.shakatahEnabled 
                            ? "border-primary/50 bg-primary/5" 
                            : "hover:bg-muted/50"
                        }`}
                      >
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate">{product.title}</h4>
                          <p className="text-sm text-muted-foreground">{formatINR(Number(product.price))}</p>
                        </div>
                        <Switch
                          checked={product.shakatahEnabled}
                          onCheckedChange={() => {
                            console.log("Switch onCheckedChange triggered for:", product.id);
                            handleToggleProduct(product.id, product.shakatahEnabled);
                          }}
                          aria-label={`Enable split payment for ${product.title}`}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Info message */}
              <div className="p-3 rounded-lg bg-muted/50 border border-muted">
                <p className="text-sm text-muted-foreground flex items-start gap-2">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    You can add or remove products, or change split payment timelines anytime later from the <strong>Settings</strong> section in your dashboard.
                  </span>
                </p>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => handleStepChange("connect")}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={() => handleStepChange("widget")}
                  className="flex-1"
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "widget" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {widgetInstalled ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <Code className="h-5 w-5" />
                )}
                {widgetInstalled ? "Widget Installed" : "Install the Widget"}
              </CardTitle>
              <CardDescription>
                {widgetInstalled 
                  ? "The split payment button will appear on your enabled products"
                  : "Choose where to display the split payment button on your product pages"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!widgetInstalled ? (
                <>
                  {/* Position Selection */}
                  <div className="space-y-4">
                    <Label className="text-sm font-medium">Button Position</Label>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {/* Top Left of Image Option */}
                      <div
                        className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          widgetPosition === "image-top-left" 
                            ? "border-primary bg-primary/5" 
                            : "border-muted hover:border-muted-foreground/50"
                        }`}
                        onClick={() => setWidgetPosition("image-top-left")}
                      >
                        <div className="space-y-3">
                          <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-muted">
                            <img 
                              src={sampleProductImage} 
                              alt="Sample product" 
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute top-2 left-2 px-2 py-1 bg-primary rounded-md text-[10px] text-white font-medium flex items-center gap-1 shadow-lg">
                              👥 Split Payment
                            </div>
                          </div>
                          <div>
                            <p className="font-medium text-sm">Top Left of Image</p>
                            <p className="text-xs text-muted-foreground">
                              Badge overlays product image
                            </p>
                          </div>
                        </div>
                        {widgetPosition === "image-top-left" && (
                          <CheckCircle className="absolute top-2 right-2 h-5 w-5 text-primary" />
                        )}
                      </div>

                      {/* Next to Title Option */}
                      <div
                        className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          widgetPosition === "next-to-title" 
                            ? "border-primary bg-primary/5" 
                            : "border-muted hover:border-muted-foreground/50"
                        }`}
                        onClick={() => setWidgetPosition("next-to-title")}
                      >
                        <div className="space-y-3">
                          <div className="flex gap-3">
                            <div className="w-20 aspect-square rounded-lg overflow-hidden bg-muted flex-shrink-0">
                              <img 
                                src={sampleProductImage} 
                                alt="Sample product" 
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 space-y-2 pt-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="h-4 w-20 bg-muted rounded" />
                                <div className="px-1.5 py-0.5 bg-primary rounded text-[8px] text-white font-medium">
                                  👥 Split
                                </div>
                              </div>
                              <div className="h-3 w-16 bg-muted rounded" />
                              <div className="h-3 w-12 bg-muted rounded" />
                            </div>
                          </div>
                          <div>
                            <p className="font-medium text-sm">Next to Product Title</p>
                            <p className="text-xs text-muted-foreground">
                              Button beside product info
                            </p>
                          </div>
                        </div>
                        {widgetPosition === "next-to-title" && (
                          <CheckCircle className="absolute top-2 right-2 h-5 w-5 text-primary" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* One-Click Install Button */}
                  <Button 
                    onClick={handleInstallWidget}
                    disabled={installingWidget}
                    size="lg"
                    className="w-full"
                  >
                    {installingWidget ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Rocket className="h-4 w-4 mr-2" />
                    )}
                    Install Widget Automatically
                  </Button>

                </>
              ) : (
                <Alert className="border-green-500/50 bg-green-500/10">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-600">
                    <p className="font-medium">Widget installed successfully!</p>
                    <p className="text-sm mt-1">
                      The split payment button will now appear on your {widgetPosition === "image-top-left" ? "product images" : "product titles"}.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => handleStepChange("products")}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={() => handleStepChange("complete")}
                  className="flex-1"
                >
                  {widgetInstalled ? "Complete Setup" : "Skip for Now"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "complete" && (
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">You're All Set!</CardTitle>
              <CardDescription className="text-base">
                Your 30-day free trial has started. Enjoy full access to all features!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-3">
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-green-500/5 border-green-500/20">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>Business details saved</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-green-500/5 border-green-500/20">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>Shopify API connected</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-green-500/5 border-green-500/20">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>{enabledProductsCount} product{enabledProductsCount !== 1 ? "s" : ""} enabled for split payments</span>
                </div>
                <div className={`flex items-center gap-3 p-3 rounded-lg border ${widgetInstalled ? 'bg-green-500/5 border-green-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
                  {widgetInstalled ? (
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  )}
                  <span>{widgetInstalled ? 'Widget installed on your store' : 'Widget installation skipped'}</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-primary/5 border-primary/20">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span>30-day trial activated (up to 10 splits)</span>
                </div>
              </div>

              {!widgetInstalled && (
                <Alert className="border-amber-500/50 bg-amber-500/10">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700">
                    <p className="font-medium">Widget not yet installed</p>
                    <p className="text-sm mt-1">
                      You can install the widget later from <strong>Dashboard → Settings → Widget</strong>, or manually add the code to your Shopify theme.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {widgetInstalled && (
                <Alert className="border-green-500/50 bg-green-500/10">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">
                    <p className="font-medium">Widget is live!</p>
                    <p className="text-sm mt-1">
                      The split payment button is now showing on your enabled products.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              <Button 
                onClick={handleComplete}
                className="w-full"
                size="lg"
              >
                Go to Dashboard
                <Rocket className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MerchantOnboarding;
